/**
 * Utilidad para comprimir y convertir imágenes a WebP en el navegador
 * Usa Canvas API para la conversión y compresión
 */

/**
 * Comprime una imagen y la convierte a formato WebP
 * @param {File} file - Archivo de imagen original (JPG, JPEG, PNG)
 * @param {Object} options - Opciones de compresión
 * @param {number} options.quality - Calidad WebP (0-1), por defecto 0.82
 * @param {number} options.maxWidthOrHeight - Ancho o alto máximo, por defecto sin límite
 * @returns {Promise<File>} - Archivo comprimido en formato WebP
 */
export async function compressImageToWebP(file, options = {}) {
  const {
    quality = 0.82, // 82% de calidad por defecto
    maxWidthOrHeight = undefined, // Sin límite por defecto
  } = options;

  return new Promise((resolve, reject) => {
    // Validar que sea un archivo de imagen
    if (!file.type.startsWith('image/')) {
      reject(new Error('El archivo no es una imagen válida'));
      return;
    }

    const reader = new FileReader();
    
    reader.onerror = () => {
      reject(new Error('Error al leer el archivo'));
    };

    reader.onload = (e) => {
      const img = new Image();
      
      img.onerror = () => {
        reject(new Error('Error al cargar la imagen'));
      };

      img.onload = () => {
        try {
          // Crear canvas
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          // Calcular dimensiones
          let width = img.width;
          let height = img.height;

          // Redimensionar si es necesario
          if (maxWidthOrHeight) {
            if (width > height) {
              if (width > maxWidthOrHeight) {
                height = (height * maxWidthOrHeight) / width;
                width = maxWidthOrHeight;
              }
            } else {
              if (height > maxWidthOrHeight) {
                width = (width * maxWidthOrHeight) / height;
                height = maxWidthOrHeight;
              }
            }
          }

          // Configurar canvas
          canvas.width = width;
          canvas.height = height;

          // Dibujar imagen en canvas
          ctx.drawImage(img, 0, 0, width, height);

          // Convertir a WebP
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Error al convertir la imagen a WebP'));
                return;
              }

              // Obtener nombre base (sin extensión)
              const originalName = file.name;
              const nameWithoutExt = originalName.substring(0, originalName.lastIndexOf('.')) || originalName;
              
              // Crear nuevo archivo con extensión .webp
              const webpFile = new File(
                [blob],
                `${nameWithoutExt}.webp`,
                { type: 'image/webp' }
              );

              resolve(webpFile);
            },
            'image/webp',
            quality
          );
        } catch (error) {
          reject(new Error(`Error al procesar la imagen: ${error.message}`));
        }
      };

      img.src = e.target.result;
    };

    reader.readAsDataURL(file);
  });
}

/**
 * Comprime múltiples imágenes a WebP
 * @param {FileList|File[]} files - Lista de archivos de imagen
 * @param {Object} options - Opciones de compresión (ver compressImageToWebP)
 * @param {Function} onProgress - Callback de progreso (current, total)
 * @returns {Promise<File[]>} - Array de archivos comprimidos en WebP
 */
export async function compressMultipleImages(files, options = {}, onProgress = null) {
  const filesArray = Array.from(files);
  const compressedFiles = [];
  
  for (let i = 0; i < filesArray.length; i++) {
    try {
      const compressed = await compressImageToWebP(filesArray[i], options);
      compressedFiles.push(compressed);
      
      if (onProgress) {
        onProgress(i + 1, filesArray.length);
      }
    } catch (error) {
      console.error(`Error comprimiendo ${filesArray[i].name}:`, error);
      // Continuar con las demás imágenes
    }
  }
  
  return compressedFiles;
}

/**
 * Obtiene información sobre la compresión realizada
 * @param {File} originalFile - Archivo original
 * @param {File} compressedFile - Archivo comprimido
 * @returns {Object} - Información de compresión
 */
export function getCompressionInfo(originalFile, compressedFile) {
  const originalSize = originalFile.size;
  const compressedSize = compressedFile.size;
  const reduction = ((1 - compressedSize / originalSize) * 100).toFixed(1);
  
  return {
    originalName: originalFile.name,
    compressedName: compressedFile.name,
    originalSize: (originalSize / 1024).toFixed(2), // KB
    compressedSize: (compressedSize / 1024).toFixed(2), // KB
    reduction: `${reduction}%`,
    originalFormat: originalFile.type,
    compressedFormat: compressedFile.type,
  };
}

export default {
  compressImageToWebP,
  compressMultipleImages,
  getCompressionInfo,
};
